/* =========================================================
   Image Text Scroll — custom element
   - Desktop: IntersectionObserver detecta qué bloque está
     en el centro del viewport y activa la imagen correspondiente
   - Mobile: sincroniza dots con scroll horizontal del carrusel
   ========================================================= */

class ImageTextScroll extends HTMLElement {
  constructor() {
    super();
    this.activeIndex = 0;
    this.observer = null;
    this.scrollListener = null;
    this.resizeListener = null;
    this.isMobile = window.matchMedia('(max-width: 1099px)').matches;
  }

  connectedCallback() {
    this.imageWrappers = this.querySelectorAll('.image-text-scroll__image-wrapper');
    this.blocks = this.querySelectorAll('.image-text-scroll__block');
    this.dots = this.querySelectorAll('.image-text-scroll__dot');
    this.textColumn = this.querySelector('.image-text-scroll__text-column');

    if (this.blocks.length === 0) return;

    this.init();
    this.bindResize();
  }

  disconnectedCallback() {
    this.cleanup();
    if (this.resizeListener) {
      window.removeEventListener('resize', this.resizeListener);
    }
  }

  init() {
    this.cleanup();

    if (this.isMobile) {
      this.initMobile();
    } else {
      this.initDesktop();
    }

    // Click en dots → scroll al slide correspondiente (mobile)
    this.dots.forEach((dot) => {
      dot.addEventListener('click', this.handleDotClick.bind(this));
    });
  }

  cleanup() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
    if (this.scrollListener && this.textColumn) {
      this.textColumn.removeEventListener('scroll', this.scrollListener);
      this.scrollListener = null;
    }
  }

  // ----- DESKTOP: IntersectionObserver -----
  initDesktop() {
    // El threshold se calcula para detectar cuándo el bloque cruza el centro del viewport
    const options = {
      root: null,
      // rootMargin negativo arriba y abajo crea una "ventana" de detección en el centro
      rootMargin: '-40% 0px -40% 0px',
      threshold: 0,
    };

    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const index = parseInt(entry.target.dataset.blockIndex, 10);
          this.setActive(index);
        }
      });
    }, options);

    this.blocks.forEach((block) => this.observer.observe(block));
  }

  // ----- MOBILE: scroll del carrusel sincroniza dots -----
  initMobile() {
    if (!this.textColumn) return;

    let rafId = null;

    this.scrollListener = () => {
      if (rafId) cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        const scrollLeft = this.textColumn.scrollLeft;
        const blockWidth = this.textColumn.clientWidth;
        const index = Math.round(scrollLeft / blockWidth);
        if (index !== this.activeIndex && index >= 0 && index < this.blocks.length) {
          this.setActive(index);
        }
      });
    };

    this.textColumn.addEventListener('scroll', this.scrollListener, { passive: true });
  }

  // ----- Click en dot → scroll al slide -----
  handleDotClick(e) {
    const index = parseInt(e.currentTarget.dataset.dotIndex, 10);
    if (isNaN(index)) return;

    if (this.isMobile && this.textColumn) {
      const blockWidth = this.textColumn.clientWidth;
      this.textColumn.scrollTo({
        left: index * blockWidth,
        behavior: 'smooth',
      });
    } else {
      // Desktop: scroll al bloque correspondiente
      const block = this.blocks[index];
      if (block) {
        block.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  // ----- Activar imagen + bloque + dot por índice -----
  setActive(index) {
    if (index === this.activeIndex) return;
    this.activeIndex = index;

    this.imageWrappers.forEach((wrapper, i) => {
      wrapper.classList.toggle('is-active', i === index);
    });

    this.blocks.forEach((block, i) => {
      block.classList.toggle('is-active', i === index);
    });

    this.dots.forEach((dot, i) => {
      const isActive = i === index;
      dot.classList.toggle('is-active', isActive);
      dot.setAttribute('aria-selected', isActive ? 'true' : 'false');
    });
  }

  // ----- Resize: si cambia entre mobile/desktop, reinicializar -----
  bindResize() {
    let resizeTimer;
    this.resizeListener = () => {
      clearTimeout(resizeTimer);
      resizeTimer = setTimeout(() => {
        const wasMobile = this.isMobile;
        this.isMobile = window.matchMedia('(max-width: 1099px)').matches;
        if (wasMobile !== this.isMobile) {
          this.init();
        }
      }, 200);
    };
    window.addEventListener('resize', this.resizeListener);
  }
}

if (!customElements.get('image-text-scroll')) {
  customElements.define('image-text-scroll', ImageTextScroll);
}